/**
 * 资源准入审核 · 详情：三级时间线 + 审核推进 + 上架 + 清退处置。
 *
 * ══ 这一屏的职责 ══
 *
 *   1. **把「这个主体到底审到哪一步」讲清楚**：三级逐级显示结论、审核人（含工号）、时间、
 *      要件 / 核验结论与备注；**做过的级次照实列出，没做过的级次显示「未审核」**——
 *      不留空、不显示 0。「没做过」与「做了但结论是 0 条」是完全不同的两件事，
 *      前者要继续推进，后者根本不存在这种状态；
 *   2. **三级内容来自接口的 `tiers`**：每级 `scope`（形式审查 / 实质审查 / 现场核验）与
 *      `evidenceHint` 由 `core/resourceAdmission.ts` 的 `tierDefinitions` 下发，界面不硬编码；
 *   3. **只给出合法的下一步**：可推进的级次由服务端 `canReviewTiers` 给出（它内部用
 *      `assertTierProgress()` 逐级试过）。界面**不自己推算「下一级是第几级」**——
 *      跳级的入口根本不存在，而不是「点了才被拒」；
 *   4. **上架与清退的区别对待**：上架只在 `admitted` 时出现；非 `admitted` 时**说明原因**
 *      （不隐藏、也不假装可用）。清退则必须先看到该主体已发生的预约数量与列表，
 *      **未确认处置不得直接清退**；
 *   5. **不碰成员个体数据**：清退清单里接口刻意不含成员姓名（审核员的 scope 是 `none`），
 *      界面也不许自行补成员信息。
 *
 * 服务端的拒绝一律**照实显示原文**（409 跳级 / 终态、400 参数、403 权限、429 限流）：
 * 审核落库失败却被渲染成成功，是这一屏最不能犯的错。
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi, ApiError } from '../lib/api.ts';
import type { ResourceAdmissionRecord, ResourceSubjectDetail, ResourceTierDefinition } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Badge, Card, PageHead, PermissionBlock } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  admissionStatusTone,
  bookableNotice,
  bookingStatusText,
  formatDateTime,
  licenseStateTone,
  reportField,
  reviewUrgencyNotice,
} from '../lib/format.ts';

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

/** 审核结论选项：取值即接口的 `admissionDecisions`，标签与 `decisionLabels` 同一张表。 */
const DECISION_OPTIONS: { value: 'pass' | 'reject' | 'need_more'; label: string }[] = [
  { value: 'pass', label: '通过' },
  { value: 'reject', label: '否决' },
  { value: 'need_more', label: '需补充材料' },
];

/** 复评结论：与 `core/resourceAdmission.ts` 的 `reviewConclusions` 一致（ok 合格 / issue 发现问题）。 */
const CONCLUSION_OPTIONS: { value: 'ok' | 'issue'; label: string }[] = [
  { value: 'ok', label: '合格（顺延下次复评）' },
  { value: 'issue', label: '发现问题（应转暂停或清退）' },
];

function decisionTone(decision: string): 'ok' | 'danger' | 'warn' | 'ghost' {
  if (decision === 'pass') return 'ok';
  if (decision === 'reject') return 'danger';
  if (decision === 'need_more') return 'warn';
  return 'ghost';
}

/**
 * 某一级次的整体状态。
 *
 * 判定规则与状态机同义：出现过否决即「已否决」（终态，不会再有通过记录）；
 * 出现过通过即「已通过」；只有「需补充材料」时按服务端给出的结论名显示；没有记录即「未审核」。
 */
function tierStatusOf(records: ResourceAdmissionRecord[]): { label: string; tone: 'ok' | 'danger' | 'warn' | 'ghost' } {
  if (records.length === 0) return { label: '未审核', tone: 'ghost' };
  if (records.some((r) => r.decision === 'reject')) return { label: '已否决', tone: 'danger' };
  if (records.some((r) => r.decision === 'pass')) return { label: '已通过', tone: 'ok' };
  const last = records[records.length - 1];
  return { label: last.decisionLabel, tone: 'warn' };
}

function Value({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="kv-l">{label}</div>
      <div className="kv-v text">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 三级时间线
 * ------------------------------------------------------------------ */

/** 单条审核记录：结论、审核人（姓名 + 工号）、时间、要件 / 核验结论、备注。 */
function AdmissionRecordBlock({ record }: { record: ResourceAdmissionRecord }) {
  return (
    <div className="kv">
      <Value label="结论">
        <Badge tone={decisionTone(record.decision)}>{record.decisionLabel}</Badge>
        <span className="muted text-tiny mono-num"> tier={record.tier}</span>
      </Value>
      <Value label="审核人（姓名 · 工号）">
        {reportField(record.reviewerName)}
        <span className="muted mono-num"> · {reportField(record.reviewerId)}</span>
      </Value>
      <Value label="审核时间">{formatDateTime(record.reviewedAt)}</Value>
      <Value label="要件 / 核验结论">{record.evidence.trim() ? record.evidence : MISSING_TEXT}</Value>
      <Value label="备注">{record.note.trim() ? record.note : MISSING_TEXT}</Value>
    </div>
  );
}

/**
 * 一级 / 二级 / 三级的时间线区块。`def` 来自接口下发的 `tiers`（含 scope 与要件提示）。
 *
 * 未审核时**显式写出「未审核」**，并说明「不是 0 条通过」——留空会让人以为是界面没渲染出来。
 */
function TierBlock({
  def,
  records,
  tier1Ready,
}: {
  def: ResourceTierDefinition;
  records: ResourceAdmissionRecord[];
  tier1Ready?: { ready: boolean; reason: string };
}) {
  const status = tierStatusOf(records);
  return (
    // 容器用 .stack（分节间距），分隔线单独用 .divider（它是 1px 的线，**不能当容器用**）
    <div className="stack">
      <div className="divider" />
      <div className="card-tt">
        <span className="bar" />
        {def.label}
        <small>
          本级 scope（随接口下发）：{def.scope}
        </small>
      </div>
      <div className="kv">
        <Value label="本级状态">
          <Badge tone={status.tone}>{status.label}</Badge>
          <span className="muted text-tiny"> 记录 {records.length} 条</span>
        </Value>
        <Value label="本级审什么（接口下发的要求）">{def.evidenceHint}</Value>
      </div>

      {def.tier === 1 && tier1Ready !== undefined && !tier1Ready.ready && (
        <div className="alert-chip warn">
          <b>一级通过的前置条件尚未满足</b>
          <div>
            服务端判定：{tier1Ready.reason}。此时记「一级通过」会被服务端直接拒绝（一级审的就是主体资格文件齐备且在有效期）。
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <EmptyBox
          title="未审核"
          desc="这一级还没有任何审核记录：主体尚未推进到本级，或本级结论还没提交。这里显示的不是「0 条结论」——「没做过」与「结果是零」是两件事。"
        />
      ) : (
        records.map((record) => <AdmissionRecordBlock key={record.id} record={record} />)
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 审核动作（只给合法的下一步）
 * ------------------------------------------------------------------ */

function ReviewPanel({
  detail,
  tierDef,
  onDone,
}: {
  detail: ResourceSubjectDetail;
  tierDef: ResourceTierDefinition;
  onDone: (toast: Toast) => void;
}) {
  const [decision, setDecision] = useState<'pass' | 'reject' | 'need_more'>('pass');
  const [evidence, setEvidence] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.reviewResourceSubject(detail.item.id, {
        tier: tierDef.tier,
        decision,
        ...(evidence.trim() ? { evidence: evidence.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone({
        text:
          `已记录：${result.admission.tierLabel} · ${result.admission.decisionLabel}（主体「${result.item.name}」→ ` +
          `${result.item.statusLabel} · ${result.item.completedTierText}）。${result.nextStep}`,
        tone: 'ok',
      });
    } catch (err) {
      // 409（跳级 / 终态 / 证照不合格）与 400（三级通过未填核验结论）都带着「为什么」，
      // 原样显示，绝不把被拒绝的审核渲染成已落库。
      setError(
        err instanceof ApiError
          ? `审核未落库（${err.status}）：${err.message}`
          : '审核请求失败，请确认后端服务可用',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">
        提交审核 · {tierDef.label}（{tierDef.scope}）
      </div>
      <div className="dp-s">
        本次只能审这一级 —— 它是服务端按当前状态算出的<b>唯一合法级次</b>（不能跳级、已通过的级次不做重复审核）。
        结论与要件会连同您的姓名 + 角色与工号写入审计链。本级要求：{tierDef.evidenceHint}
        {tierDef.tier === 3 ? '。三级为实地核验，记「通过」时必须填写核验结论（服务端会拒绝空结论的通过）。' : '。'}
      </div>
      <div className="grid2">
        <div className="field">
          <label className="field-label" htmlFor="rv-decision">
            结论
          </label>
          <select
            id="rv-decision"
            className="field-input"
            value={decision}
            onChange={(e) => setDecision(e.target.value === 'reject' ? 'reject' : e.target.value === 'need_more' ? 'need_more' : 'pass')}
          >
            {DECISION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="rv-evidence">
            要件 / 核验结论（三级通过时必填）
          </label>
          <input
            id="rv-evidence"
            className="field-input"
            placeholder="例如：营业执照与执业许可证齐备，有效期至 2027-06-30"
            value={evidence}
            maxLength={4000}
            onChange={(e) => setEvidence(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="rv-note">
          备注（可选，例如需要补充的具体材料）
        </label>
        <input
          id="rv-note"
          className="field-input"
          placeholder="会与结论一并留痕"
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void submit()}>
          {busy ? '正在提交…' : `提交 ${tierDef.label}`}
        </button>
        <span className="text-tiny muted">被服务端拒绝时下方会显示原文，界面不做乐观更新。</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 清退处置（本页重点）
 * ------------------------------------------------------------------ */

/**
 * 暂停 / 清退。
 *
 * ══ 为什么必须先把预约摆出来 ══
 *
 * 「不合格主体清退」若只是一次状态变更，成员手里已经约好的号就会凭空消失，
 * 平台只会得到投诉。因此服务端在清退接口里回报该主体**已发生（未取消）的预约**，
 * 界面把它摆在动作之前，并且**未确认处置不让点清退**——一清了之在数据上是干净的，
 * 在业务上是事故。
 */
function DisposalPanel({ detail, onDone }: { detail: ResourceSubjectDetail; onDone: (toast: Toast) => void }) {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState<'suspend' | 'revoke' | null>(null);
  const [error, setError] = useState('');

  const pending = detail.pendingBookings;
  const subject = detail.item;
  const terminal = subject.status === 'revoked';

  const submit = async (mode: 'suspend' | 'revoke'): Promise<void> => {
    setBusy(mode);
    setError('');
    try {
      const result = await adminApi.revokeResourceSubject(subject.id, mode, reason.trim());
      onDone({
        text:
          `${mode === 'suspend' ? '已暂停' : '已清退'}「${result.item.name}」→ ${result.item.statusLabel}；` +
          `接口回报该主体已发生预约 ${result.bookings.total} 条（未取消）· 受影响服务资源 ${result.affectedResources.length} 项。` +
          `${result.note}`,
        tone: 'ok',
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${mode === 'suspend' ? '暂停' : '清退'}被拒绝（${err.status}）：${err.message}`
          : '处置请求失败，请确认后端服务可用',
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">清退处置 · {subject.name}</div>
      <div className="dp-s">
        清退不是一次普通的状态变更：该主体名下<b>已经发生的预约</b>必须先在业务上处置掉
        （改约 / 通知成员 / 协调其它机构），否则成员手里已约好的号会凭空消失 ——
        数据上是干净的，业务上是事故。因此下面先列出接口回报的预约数量与清单，
        <b>未确认处置不得直接清退</b>。暂停 = 暂时停止可约（复评合格后可恢复推进）；清退 = 不再合作（终态）。
      </div>

      <div className="kv">
        <Value label="接口回报：已发生的预约（未取消）">
          <b className={pending.total > 0 ? 'hl-w' : ''}>{pending.total} 条</b>
          <span className="muted text-tiny">（该数字来自接口，界面不自行统计）</span>
        </Value>
        <Value label="当前可约的服务资源">
          {detail.bookableResourceCount} 项
          <span className="muted text-tiny">（清退后这些资源会同时失去可约资格，未结束号源被关停）</span>
        </Value>
      </div>

      {pending.total === 0 ? (
        <EmptyBox
          title="该主体名下当前没有未处置的预约（接口回报 0 条）"
          desc="这是接口返回的真实计数：未取消的预约为 0 条，不是「读不到」。清退后可直接对外宣告结果 —— 但仍需您确认这一点，因为清退不可撤销。"
        />
      ) : (
        <>
          <div className="alert-chip warn">
            <b>清退后需先处置这些预约，否则受损的是成员</b>
            <div>
              该主体名下尚有 <b>{pending.total}</b> 条已发生的预约未处置。请先逐一改约或通知成员，
              再对外宣告清退结果；一旦清退，关联资源立即不可约、未结束号源会被关停。
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>工单标识</th>
                  <th>状态</th>
                  <th>资源名</th>
                  <th>时间</th>
                </tr>
              </thead>
              <tbody>
                {pending.items.map((booking) => (
                  <tr key={booking.id}>
                    <td className="mono-num">{booking.id}</td>
                    <td>{bookingStatusText(booking.status)}</td>
                    <td>{reportField(booking.resourceName)}</td>
                    <td className="mono-num">{formatDateTime(booking.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pending.total > pending.items.length && (
            <div className="note-src">
              接口本次只下发前 {pending.items.length} 条（共 {pending.total} 条）；完整清单请按工单接口逐条核对。
            </div>
          )}
          <div className="note-src">
            <b>为什么清单里没有成员姓名</b>
            <br />
            审核员的可见范围是 <span className="mono-num">none</span>：他审的是机构与专家的资质，
            <b>不接触成员个体数据</b>。接口因此只回报工单标识、状态、资源名与时间 ——
            界面也不得自行补成员信息，这是权限模型的边界，不是界面漏了字段。
          </div>
        </>
      )}

      {terminal ? (
        <EmptyBox
          title="该主体已被清退（终态）"
          desc="已被清退的主体不再提供暂停 / 清退入口：服务端会直接拒绝（该主体已被清退，无需重复清退）。如需重新合作，必须作为新主体重新送审。"
        />
      ) : (
        <>
          <div className="field">
            <label className="field-label" htmlFor="rv-reason">
              处置原因（必填；会进审计链与风险备忘）
            </label>
            <input
              id="rv-reason"
              className="field-input"
              placeholder="例如：复评发现执业许可已过期，且一起履约投诉未闭环"
              value={reason}
              maxLength={1000}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="rv-ack">
              <input
                id="rv-ack"
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />{' '}
              {pending.total > 0
                ? `我已查看上述 ${pending.total} 条已发生的预约，并确认已逐一处置（改约 / 通知成员 / 协调其它机构）后才执行清退`
                : '我已确认该主体名下当前没有未处置的预约，并知悉清退不可撤销（关联资源会立即变为不可约）'}
            </label>
          </div>

          {error && <div className="field-error">{error}</div>}
          <div className="row">
            <button
              type="button"
              className="btn sm"
              disabled={busy !== null || !acknowledged || reason.trim().length === 0}
              onClick={() => void submit('suspend')}
            >
              {busy === 'suspend' ? '正在提交…' : '暂停该主体'}
            </button>
            <button
              type="button"
              className="btn danger sm"
              disabled={busy !== null || !acknowledged || reason.trim().length === 0}
              onClick={() => void submit('revoke')}
            >
              {busy === 'revoke' ? '正在提交…' : '清退该主体'}
            </button>
            <span className="text-tiny muted">
              两个按钮都要求「先勾选确认 + 填原因」：未确认处置时它们是不可点的（不是点了才被拒）。
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 页面
 * ------------------------------------------------------------------ */

export function ResourceDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id ?? '';
  const detail = useLoad((signal) => adminApi.resourceSubject(id, signal));
  /** 权限清单来自 /api/admin/me：动作区是否出现由此决定，不按角色名硬编码。 */
  const me = useLoad((signal) => adminApi.me(signal));
  const [toast, setToast] = useState<Toast | null>(null);

  if (detail.loading) return <LoadingBox text="正在加载主体详情…" />;

  if (detail.error) {
    const err = detail.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div>
          <PageHead title="无权查看该主体" sub="403 · 权限拦截" />
          <Card>
            <PermissionBlock
              title="当前角色没有「resource:approve」权限"
              desc={`服务端返回：${err.message}。资源准入需要 resource:approve 权限点；缺该权限点的角色（运营 / 健康管家 / 专家 / 财务 / 数据运营 / 中台管理员）连主体详情都读不到。界面不会把这次拦截显示成「主体不存在」。`}
            />
            <div className="sp12" />
            <button type="button" className="btn" onClick={() => navigate('/resources')}>
              返回被审主体列表
            </button>
          </Card>
        </div>
      );
    }
    if (err instanceof ApiError && err.status === 404) {
      return (
        <div>
          <PageHead title="被审主体不存在" sub="404" />
          <ErrorBox message={`服务端返回：${err.message}。该主体可能在库中不存在，或编号有误。`} />
          <div className="sp12" />
          <button type="button" className="btn" onClick={() => navigate('/resources')}>
            返回被审主体列表
          </button>
        </div>
      );
    }
    return <ErrorBox message={errorText(err)} onRetry={detail.reload} />;
  }

  if (!detail.data) return <ErrorBox message="主体详情为空" onRetry={detail.reload} />;

  // 先收到局部常量：属性收窄在回调 / JSX 里会被 TypeScript 丢弃，用局部常量更稳。
  const data = detail.data;
  const { item, tiers, timeline, tier1Ready, tierProgress, resources, bookableResourceCount, pendingBookings } = data;
  const urgency = reviewUrgencyNotice(item);
  const bookable = bookableNotice(item);
  const canApprove = (me.data?.permissions ?? []).includes('resource:approve');
  const tierRecords = (tier: number): ResourceAdmissionRecord[] => timeline.filter((record) => record.tier === tier);
  const reassessRecords = timeline.filter((record) => record.tier === 0);
  /** 可推进的级次由服务端给出；界面只把它映射回接口下发的三级定义，不自己推算。 */
  const reviewTierDef = tiers.find((def) => def.tier === data.canReviewTiers[0]) ?? null;

  const handleDone = (result: Toast): void => {
    setToast(result);
    detail.reload();
    window.setTimeout(() => setToast(null), 10000);
  };

  return (
    <div>
      <PageHead
        title="资源准入审核 · 详情"
        sub={`${item.name} · ${item.subjectTypeLabel}（${item.subjectType}） · ${item.categoryName} · ${item.city || MISSING_TEXT}`}
        actions={
          <button type="button" className="btn sm" onClick={() => navigate('/resources')}>
            返回被审主体列表
          </button>
        }
      />

      <Card title="主体信息" sub="来自被审主体表，未做二次加工；缺失字段显示「未提供」，不用 0 冒充">
        <div className="kv">
          <Value label="主体名称">{item.name}</Value>
          <Value label="主体类型">
            {item.subjectTypeLabel}
            <span className="muted text-tiny mono-num"> subjectType={item.subjectType}</span>
          </Value>
          <Value label="服务分类">
            {item.categoryName}
            <span className="muted text-tiny mono-num"> category={item.category}</span>
          </Value>
          <Value label="城市">{item.city.trim() ? item.city : MISSING_TEXT}</Value>
          <Value label="准入状态">
            <Badge tone={admissionStatusTone(item.status)}>{item.statusLabel}</Badge>
            <span className="muted text-tiny mono-num"> status={item.status}</span>
          </Value>
          <Value label="已完成到第几级">
            <span className="mono-num">
              {item.completedTiers} / {tiers.length}
            </span>
            <span className="muted text-tiny"> · {item.completedTierText}</span>
          </Value>
          <Value label="执照号（一级审核对象）">
            {item.licenseNo.trim() ? item.licenseNo : MISSING_TEXT}
          </Value>
          <Value label="执照有效期">
            {item.licenseExpiresAt ? formatDateTime(item.licenseExpiresAt) : MISSING_TEXT}
            <span className="muted text-tiny"> · </span>
            <Badge tone={licenseStateTone(item.licenseState)}>{item.licenseStateLabel}</Badge>
          </Value>
          <Value label="风险备注 riskNote">{item.riskNote.trim() ? item.riskNote : MISSING_TEXT}</Value>
          <Value label="下次复评">{item.nextReviewAt ? formatDateTime(item.nextReviewAt) : '未安排复评'}</Value>
          <Value label="复评紧迫度（接口判定）">
            <Badge tone={urgency.tone}>{urgency.text}</Badge>
          </Value>
          <Value label="登记 / 最近更新">
            {formatDateTime(item.createdAt)} · {formatDateTime(item.updatedAt)}
          </Value>
        </div>
        <div className="note-src">
          <b>尽调结论（服务端原文）</b>
          <br />
          {item.dueDiligence}
        </div>
      </Card>

      <Card
        title="三级审核时间线"
        sub={`逐级列出结论、审核人、时间、要件与备注；未做的级次显示「未审核」 · 共 ${timeline.length} 条记录`}
      >
        {tiers.map((def) => (
          <TierBlock
            key={def.tier}
            def={def}
            records={tierRecords(def.tier)}
            {...(def.tier === 1 ? { tier1Ready } : {})}
          />
        ))}

        <div className="stack">
          <div className="divider" />
          <div className="card-tt">
            <span className="bar" />
            持续动态评估 · 复评记录
            <small>不占三级序位（tier = 0），复评不合格应转暂停或清退</small>
          </div>
          {reassessRecords.length === 0 ? (
            <EmptyBox
              title="暂无复评记录"
              desc="该主体还没有任何复评记录。定期复评与触发式复评都会写入这里，并按接口回报的下次复评时间顺延。"
            />
          ) : (
            reassessRecords.map((record) => <AdmissionRecordBlock key={record.id} record={record} />)
          )}
        </div>

        <div className="note-src">
          <b>进度归并（来自接口 tierProgress）</b>
          <br />
          {tierProgress.map((row) => (
            <span key={row.tier}>
              {row.label}：{row.passed ? '已通过' : row.rejected ? '已否决' : '未通过'} · {row.records} 条记录
              {row.lastReviewedAt ? ` · 最近 ${formatDateTime(row.lastReviewedAt)}` : ' · 尚无记录（未审核）'}
              <br />
            </span>
          ))}
        </div>
      </Card>

      <Card title="可约状态" sub="判据由服务端给出：只有三级通过（已准入 / 已上架）才可约">
        <div className={`alert-chip ${bookable.bookable ? 'ok' : bookable.tone === 'warn' ? 'warn' : 'danger'}`}>
          <b>{bookable.text}</b>
          <div>
            {bookable.bookable
              ? '该主体已完成三级准入：其名下服务资源可能出现可约号源（上架后成员侧才看到）。'
              : `该主体当前不可约：${bookable.reason}。资源目录与预约接口都以服务端的可约判据为准，不靠界面隐藏按钮。`}
          </div>
        </div>
        <div className="kv">
          <Value label="可约的服务资源数（接口算好）">{bookableResourceCount} 项</Value>
          <Value label="归属本主体的服务资源">{resources.length} 项</Value>
        </div>
        {resources.length === 0 ? (
          <EmptyBox
            title="该主体名下暂无归属的服务资源"
            desc="主体通过三级审核并上架时，服务端会登记已归属它的资源，以及名字能对上号的无主资源；两者都没有时，成员侧不会出现它的号源。"
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>资源名</th>
                  <th>机构</th>
                  <th>分类</th>
                  <th>是否可约</th>
                  <th>尽调结论</th>
                </tr>
              </thead>
              <tbody>
                {resources.map((resource) => (
                  <tr key={resource.id}>
                    <td>{resource.name}</td>
                    <td>{reportField(resource.institution)}</td>
                    <td>{reportField(resource.category)}</td>
                    <td>
                      <Badge tone={resource.bookable ? 'ok' : 'danger'}>{resource.bookable ? '可约' : '不可约'}</Badge>
                    </td>
                    <td className="muted text-tiny">{reportField(resource.dueDiligence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {canApprove ? (
        <>
          <Card title="审核动作" sub="只给合法的下一步 · 需要 resource:approve 权限点">
            {reviewTierDef === null ? (
              <EmptyBox
                title="当前状态下没有可推进的审核级次"
                desc={`服务端按状态机判定：状态「${item.statusLabel}」下没有任何一级可以提交。已通过的级次不做重复审核（如需记录新的核查结论请走复评）；已否决 / 已清退是终态，要重启流程必须重新送审（新建主体）。界面不会给出跳级或改判的入口。`}
              />
            ) : (
              <ReviewPanel detail={data} tierDef={reviewTierDef} onDone={handleDone} />
            )}
          </Card>

          <Card title="上架" sub="只有三级准入全部通过（已准入）才能上架">
            {item.status === 'admitted' ? (
              <>
                <div className="alert-chip warn">
                  <b>上架后成员侧才可能出现该主体的号源</b>
                  <div>上架不是「发布」，而是把已完成三级审核的资源登记为可约；在此之前资源目录里看不到它的号源。</div>
                </div>
                <ListUpPanel detail={data} onDone={handleDone} />
              </>
            ) : (
              <EmptyBox
                title={`当前状态「${item.statusLabel}」，上架入口不出现`}
                desc={
                  item.status === 'listed'
                    ? '该主体已上架（三级准入已通过），无需重复上架：服务端会拒绝重复上架。'
                    : '只有三级准入全部通过（状态为「已准入」）的主体才能上架：服务端会以 409 拒绝其它状态的上架请求。这不是界面把按钮藏起来，而是这个动作在当前状态下不存在。请先按一级 → 二级 → 三级完成准入。'
                }
              />
            )}
          </Card>

          <Card title="持续动态评估 · 复评" sub="复评时间与逾期判定来自接口；复评记录写入审核时间线">
            <div className="kv">
              <Value label="下次复评时间">
                {item.nextReviewAt ? formatDateTime(item.nextReviewAt) : '未安排复评'}
              </Value>
              <Value label="距下次复评">
                {item.daysUntilNextReview === null ? MISSING_TEXT : `${item.daysUntilNextReview} 天`}
              </Value>
              <Value label="复评紧迫度（接口判定）">
                <Badge tone={urgency.tone}>{urgency.text}</Badge>
              </Value>
            </div>
            <ReassessPanel detail={data} onDone={handleDone} />
          </Card>

          <Card title="清退处置" sub="暂停 / 清退前必须先看该主体已发生的预约；未确认处置不得直接清退">
            <DisposalPanel detail={data} onDone={handleDone} />
          </Card>
        </>
      ) : (
        <Card title="审核动作" sub="当前员工无权审核">
          <PermissionBlock
            title="当前角色没有「resource:approve」权限"
            desc={
              me.loading
                ? '正在向 /api/admin/me 校验权限点…'
                : me.error
                  ? `权限清单读取失败：${errorText(me.error)}。在读到权限之前，本页不显示审核、上架与清退入口。`
                  : '资源准入需要 resource:approve 权限点（资源审核员与超级管理员具备）。这不是界面故障，是权限模型的正常结果。'
            }
          />
        </Card>
      )}

      <div className="note-src">
        <b>为什么清退要先看预约</b>
        <br />
        「不合格主体清退」若只是一次状态变更，成员手里已经约好的号就会凭空消失，平台只会得到投诉。
        因此服务端在清退接口里回报该主体名下<b>已发生（未取消）的预约</b>，本页把它摆在动作之前，
        并且要求先确认处置：{pendingBookings.total} 条待处置预约时，未勾选确认的清退按钮是不可点的。
        清退落库后，关联资源会同时失去可约资格，未结束的号源会被关停。
      </div>

      <div className="note-src">
        <b>三级口径的来源</b>
        <br />
        本页每一级的名称、scope（形式审查 / 实质审查 / 现场核验）与要件提示都来自接口下发的
        <span className="mono-num"> tiers</span>；「能不能审这一级」来自接口的
        <span className="mono-num"> canReviewTiers</span>。界面不维护第二份三级文案，也不自己推算
        「下一级是第几级」—— 跳级的入口因此根本不存在。
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
      <div className="sp8" />
    </div>
  );
}

/**
 * 上架动作。二次确认的理由：上架会让该主体的资源**对成员开放可约**，
 * 而主体一旦被否决/清退，成员已经约好的号就会受影响。
 */
function ListUpPanel({ detail, onDone }: { detail: ResourceSubjectDetail; onDone: (toast: Toast) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    const confirmed = window.confirm(
      `确认上架「${detail.item.name}」？\n\n` +
        `当前状态：${detail.item.statusLabel} · ${detail.item.completedTierText}\n` +
        '上架后该主体名下已通过三级准入的服务资源才会对成员开放可约。',
    );
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.listResourceSubject(detail.item.id);
      onDone({
        text:
          `已上架「${result.item.name}」→ ${result.item.statusLabel}：本次登记服务资源 ${result.linkedResources} 项 · ` +
          `可约资源 ${result.bookableResourceCount} 项。${result.note}`,
        tone: 'ok',
      });
    } catch (err) {
      setError(err instanceof ApiError ? `上架被拒绝（${err.status}）：${err.message}` : '上架请求失败，请确认后端服务可用');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">上架 · {detail.item.name}</div>
      <div className="dp-s">
        上架只做两件事：把该主体名下已归属（或名字能对上号）的服务资源登记到它名下，并把主体状态置为
        「已上架可约」。<b>未通过三级准入的资源不会被登记</b>，因此上架不会让未经尽调的机构出现在成员侧。
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void submit()}>
          {busy ? '正在上架…' : '上架该主体'}
        </button>
        <span className="text-tiny muted">动作会写入审计链（含工号）与资源尽调结论。</span>
      </div>
    </div>
  );
}

/** 记录一次复评（持续动态评估）。复评结论会写入时间线，并顺延下次复评时间。 */
function ReassessPanel({ detail, onDone }: { detail: ResourceSubjectDetail; onDone: (toast: Toast) => void }) {
  const [conclusion, setConclusion] = useState<'ok' | 'issue'>('ok');
  const [nextReviewAt, setNextReviewAt] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const subject = detail.item;
  const blockedReason =
    subject.status === 'revoked'
      ? '该主体已被清退（终态），不再产生复评记录。'
      : subject.status === 'rejected'
        ? '该主体已被否决：复评仅适用于准入中的主体，如已整改请重新送审（新建主体）。'
        : '';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.reassessResourceSubject(subject.id, {
        conclusion,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(nextReviewAt ? { nextReviewAt } : {}),
      });
      onDone({
        text:
          `已记录复评（主体「${subject.name}」）：下次复评由 ${result.previousNextReviewAt ? formatDateTime(result.previousNextReviewAt) : '未安排'} ` +
          `调整为 ${formatDateTime(result.nextReviewAt)}。${result.note}`,
        tone: 'ok',
      });
    } catch (err) {
      setError(err instanceof ApiError ? `复评未落库（${err.status}）：${err.message}` : '复评请求失败，请确认后端服务可用');
    } finally {
      setBusy(false);
    }
  };

  if (blockedReason !== '') {
    return <EmptyBox title="当前状态不再受理复评" desc={blockedReason} />;
  }

  return (
    <div className="dispatch-panel">
      <div className="dp-t">记录一次复评</div>
      <div className="dp-s">
        持续动态评估的价值在于「发现问题后能立刻停止派单」，因此复评结论必须写清楚：
        选「发现问题」时应紧接着执行暂停或清退，而不是留着可约资格继续派单。
        不填下次复评时间时，服务端按默认周期顺延。
      </div>
      <div className="grid2">
        <div className="field">
          <label className="field-label" htmlFor="ra-conclusion">
            复评结论
          </label>
          <select
            id="ra-conclusion"
            className="field-input"
            value={conclusion}
            onChange={(e) => setConclusion(e.target.value === 'issue' ? 'issue' : 'ok')}
          >
            {CONCLUSION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="ra-next">
            下次复评时间（可选；只填日期即当天 23:59:59）
          </label>
          <input
            id="ra-next"
            className="field-input"
            type="date"
            value={nextReviewAt}
            onChange={(e) => setNextReviewAt(e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label className="field-label" htmlFor="ra-note">
          说明（可选）
        </label>
        <input
          id="ra-note"
          className="field-input"
          placeholder="例如：复评发现责任保险已过期，要求 15 日内补齐"
          value={note}
          maxLength={1000}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn sm" disabled={busy} onClick={() => void submit()}>
          {busy ? '正在记录…' : '记录复评'}
        </button>
      </div>
    </div>
  );
}
